import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { Message as MessageType } from '@/widget/domain/message';
import { Attachment as AttachmentType } from '@/widget/domain/attachment';
import DirectS3Uploader from '@/widget/services/DirectS3Uploader';
import swal from 'sweetalert';
import moment from 'moment';
import { useChatWidgetContext } from '@/widget/services/useChatWidgetContext';
import { useStore } from 'zustand';
import generateUUID from '@/lib/generateUUID';

export default function useMessageInput(active, addMessage, handleResize) {
  const { store, settings } = useChatWidgetContext();
  const { host, token } = settings;
  const [text, setText] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const conversationKey = useStore(store, (state) => state.activeConversationKey);

  const textarea = useRef(null);
  const textareaContainer = useRef(null);
  const fileInput = useRef(null);

  const autogrow = useCallback(() => {
    const textareaLineHeight = 15;
    const minRows = 1;
    const maxRows = 10;

    const previousRows = textarea.current.rows;
    const previousHeight = textareaContainer.current.clientHeight;
    textarea.current.rows = minRows;
    const height = textarea.current.scrollHeight - 4; // paddings
    const currentRows = height / textareaLineHeight;
    if (currentRows === previousRows) { textarea.current.rows = currentRows; }
    if (currentRows >= maxRows) {
      textarea.current.rows = maxRows;
      textarea.current.scrollTop = textarea.current.scrollHeight;
    }
    textarea.current.rows = currentRows < maxRows ? currentRows : maxRows;
    if (previousHeight !== textareaContainer.current.clientHeight) {
      handleResize(previousHeight, textareaContainer.current.clientHeight);
    }
    return true;
  }, [handleResize]);

  useEffect(() => {
    if (active) { textarea.current.focus(); }
  }, [active]);

  useEffect(() => {
    autogrow();
  }, [autogrow, text]);

  function triggerUpload() {
    if (attachments.length < 10) {
      fileInput.current.click();
    }
  }

  const handleFilePaste = useCallback((e) => {
    e.preventDefault();
    if (e.clipboardData.files.length) {
      fileInput.current.files = e.clipboardData.files;
      const paste = document.createEvent('Event');
      paste.initEvent('change', true, true);
      fileInput.current.dispatchEvent(paste);
    } else if (e.clipboardData.getData('text/plain').length) {
      const plainText = e.clipboardData.getData('text/plain');
      document.execCommand(
        'insertHTML',
        false,
        plainText,
      );
    }
  }, []);

  const handleUploaded = useCallback(async (e) => {
    if (!e.target.files && !e.target.files[0]) { return; }
    const file = e.target.files[0];
    if (file.size > 10485760) {
      swal({
        text: window.ChatWidget.i18n.t('chat_widget.errors.too_large'),
        icon: 'info',
        buttons: {
          confirm: {
            text: window.ChatWidget.i18n.t('chat_widget.ok'),
            value: true,
            visible: true,
            closeModal: true,
          },
        },
      });
    } else {
      await DirectS3Uploader.uploadFile(host, token, file).then(({ fileName, key }) => {
        const attachment: AttachmentType = {
          id: generateUUID(),
          title: fileName,
          size: file.size,
          kind: file.type,
          url: URL.createObjectURL(file),
          key,
        };
        setAttachments((prevState) => [...prevState, attachment]);
      });
    }
    e.target.value = '';
  }, [host, token]);

  function sendMessage() {
    if (!text.trim().length && !attachments.length) { return false; }

    const message: MessageType = {
      id: generateUUID(),
      date: moment(new Date()).format(),
      fromOperator: false,
      sender: null,
      sent: false,
      text,
      attachments,
    };
    setSending(true);
    addMessage(conversationKey, message);
    setSending(false);
    setText('');
    setAttachments([]);
    textarea.current.focus();

    return true;
  }

  return {
    text,
    attachments,
    sending,

    textarea,
    textareaContainer,
    fileInput,

    setText,
    triggerUpload,
    handleFilePaste,
    handleUploaded,
    sendMessage,
  };
}
